'use strict';

import has from 'lodash/has';

/**
 * Common functionality and utilities that could be
 * useful to all AceUI applications.
 */
(function () {
  window.atex = window.atex || {};
  window.atex.aceui = window.atex.aceui || {};

  if (!String.prototype.startsWith) {
    String.prototype.startsWith = function (searchString, position){
      position = position || 0;
      return this.substr(position, searchString.length) === searchString;
    };
  }

  /**
   * If there is no window.console, or a window.console with insufficient
   * support for the methods we want to use, we create our own wrapping
   * implementations, just re-using window.console.log();
   */

  window.console = window.console || { log: function () {} };

  window.console.info = window.console.info || function (s) { window.console.log(s); };
  window.console.warn = window.console.warn || function (s) { window.console.log(s); };
  window.console.error = window.console.error || function (s) { window.console.log(s); };

  /**
   * Utility functions for inspecting the current browser environment.
   */
  atex.aceui.BrowserUtils = {
    /**
     * Returns true if the current browser looks to be a Safari browser.
     * This should be true for Mobile Safari (IOS) as well as the desktop Safari.
     */
    isSafariBrowser: function () {
      if (window.navigator !== undefined && window.navigator.userAgent !== undefined) {
        var agent = navigator.userAgent.toLowerCase();
        return agent.indexOf('safari') != -1 && agent.indexOf('chrome') == -1;
      }

      return false;
    }
  };

  /**
   * Utility functions for dealing with domain objects.
   */
  atex.aceui.DomainObjectUtils = {

    /**
     * Creates and returns a proxy on top of a Domain Object that overrides setData
     * and sets the changeId parameter of setData to ownerId.
     **/
    getProxyFor: function (domainObject, ownerId) {
      if (typeof domainObject === 'undefined') {
        return undefined;
      }

      var proxy = Object.create(domainObject);

      proxy.setData = function (data) {
        return domainObject.setData.call(this, data, ownerId);
      };

      proxy.getChild = function (childName) {
        return atex.aceui.DomainObjectUtils.getProxyFor(domainObject.getChild(childName), ownerId);
      };

      Object.freeze(proxy);

      return proxy;
    },

    /**
     * Returns the domain object found in the given domain object tree at the given path.
     *
     * Parameter 'domainObject' - The domain object tree to look into for the requested domain object.
     * Parameter 'path' - The path into the domain object tree at which location to find the requested
     * domain object. Should be either a string or an array of strings. If the path is a string, the
     * '/' character functions as a separator for path segments.
     *
     * Returns undefined if the given path doesn't exist in the given domain object tree.
     *
     * Returns the input domain object if the path is empty.
     * Returns the input domain object if either the input domain object or the path is null or undefined.
     *
     * NOTE: addressing a path where individual path segments actually contain the '/' character
     * is only possible by using the array path syntax.
     *
     * Examples:
     *
     * - getDomainObjectByPath(domainObject, 'first')
     * - getDomainObjectByPath(domainObject, 'first/second')
     * - getDomainObjectByPath(domainObject, ['first'])
     * - getDomainObjectByPath(domainObject, ['first', 'second'])
     * - getDomainObjectByPath(domainObject, ['first', 'second/third'])
     */
    getDomainObjectByPath: function (domainObject, path) {
      if (domainObject == null || path == null || path.length === 0 || (path.length === 1 && path[0] === '/')) {
        return domainObject;
      }

      if (path[0] === '/') {
        path = (typeof path === 'string') ? path.substring(1) : path.slice(1);
      }

      var objectPath = (typeof path === 'string') ? path.split('/') : path;

      var pathSegments = objectPath.slice(0);
      var chain = pathSegments.slice(0, -1);

      var valueObject = domainObject;

      for (var i = 0; i < chain.length; i++) {
        var pathSegment = chain[i];

        if (!valueObject.hasChild(pathSegment)) {
          return undefined;
        }

        valueObject = valueObject.getChild(pathSegment);
      }

      return valueObject.getChild(pathSegments.pop());
    },

    /**
     * Returns the domain objects found in the given domain object tree at the given paths.
     *
     * Parameter 'domainObject' - The domain object tree to look into for the requested domain objects.
     * Parameter 'paths' - The paths into the domain object tree at which location to find the requested
     * domain objects. Should be an object where the keys are the names of requested domain objects, and
     * the values are the paths at which to find the domain objects. The paths should be either strings or arrays
     * of strings. If a path is a string, the '/' character functions as a separator for path segments.
     *
     * Returns an undefined value for any domain object for which the path doesn't exist in the given domain object tree.
     *
     * NOTE: addressing a path where individual path segments actually contain the '/' character
     * is only possible by using the array path syntax.
     *
     * Examples:
     *
     * - getMultipleDomainObjectsByPath(domainObject, {first: 'first'})
     * - getMultipleDomainObjectsByPath(domainObject, {second: 'first/second'})
     * - getMultipleDomainObjectsByPath(domainObject, {first: ['first']})
     * - getMultipleDomainObjectsByPath(domainObject, {second: ['first', 'second']})
     * - getMultipleDomainObjectsByPath(domainObject, {third: ['first', 'second/third']})
     */
    getMultipleDomainObjectsByPath: function (domainObject, paths) {
      var domainObjects = {};

      if (domainObject == null || paths == null || Object.keys(paths).length === 0) {
        return domainObjects;
      }

      Object.keys(paths).forEach(function (key) {
        domainObjects[key] = atex.aceui.DomainObjectUtils.getDomainObjectByPath(domainObject, paths[key]);
      });

      return domainObjects;
    }
  };

  /**
   * Helper property for atex.aceui.Utils.uuid.
   */
  var uuidLookUpTable = (function () {
    var table = [];

    for (var i = 0; i < 256; i++) {
      table[i] = (i < 16 ? '0' : '') + (i).toString(16);
    }

    return table;
  })();

  /**
   * Utility function(s) that doesn't really fit anywhere else.
   **/
  atex.aceui.Utils = {
    /**
     * Generates a rfc4122 compliant Universally Unique Identifier
     * compliments to http://stackoverflow.com/questions/105034/how-to-create-a-guid-uuid-in-javascript/21963136#21963136
     *
     * @returns {string}
     */
    uuid: function () {
      var d0 = Math.random() * 0xffffffff | 0;
      var d1 = Math.random() * 0xffffffff | 0;
      var d2 = Math.random() * 0xffffffff | 0;
      var d3 = Math.random() * 0xffffffff | 0;

      return uuidLookUpTable[d0 & 0xff] + uuidLookUpTable[d0 >> 8&0xff] + uuidLookUpTable[d0 >> 16&0xff] + uuidLookUpTable[d0 >> 24&0xff] + '-' +
             uuidLookUpTable[d1 & 0xff] + uuidLookUpTable[d1 >> 8&0xff] + '-' + uuidLookUpTable[d1 >> 16&0x0f|0x40] + uuidLookUpTable[d1 >> 24&0xff] + '-' +
             uuidLookUpTable[d2 & 0x3f|0x80] + uuidLookUpTable[d2 >> 8&0xff] + '-' + uuidLookUpTable[d2 >> 16&0xff] + uuidLookUpTable[d2 >> 24&0xff] +
             uuidLookUpTable[d3 & 0xff] + uuidLookUpTable[d3 >> 8&0xff] + uuidLookUpTable[d3 >> 16&0xff] + uuidLookUpTable[d3 >> 24&0xff];
    },

    /**
     * Generates a six digit random string of hexadecimal characters.
     *
     * @return {string}
     */
    randomHex: function () {
      var hexStr = (Math.random() * 0xffffff | 0).toString(16);
      return ('00000' + hexStr).slice(-6); // padding to always get 6 digits.
    }
  };

  /**
   * Utility functions for object path and data operations.
   */
  atex.aceui.ObjectUtils = {
    /**
     * Creates and returns an accessor (getter / setter) for an object with the specified path. Any data
     * set using the accessor will be set into the given object at the given path. Any data
     * read from the accessor will be read from the given object at the given path.
     *
     * Parameter 'object' - The object for which to create the accessor.
     * Parameter 'path' - The path to use for the getter / setting and the given object. Should be
     * either a string or an array of strings. If the path is a string, the '/' character functions as a
     * separator for path segments.
     *
     * By default, if a path doesn't exist in the given object when using the setter, it will be created.
     * This behaviour can be controlled by setting accessor.createpath (true / false).
     *
     * Will throw an error if the input object is undefined or null.
     */
    accessor: function (object, path) {
      if (object == null) {
        throw Error('Cannot create an object accessor for an undefined or null object!');
      }

      if (path == null || path.length === 0) {
        path = [];
      }

      var objectPath = (typeof path === 'string') ? path.split('/') : path;

      return {
        createpath: true,

        get value () {
          return atex.aceui.ObjectUtils.getByPath(object, path);
        },

        set value (val) {
          if (objectPath.length > 0) {
            if (objectPath.length > 1) {
              if (this.createpath) {
                object = atex.aceui.ObjectUtils.makeObjectStructureFromPath(object, path);
              }

              var target = atex.aceui.ObjectUtils.getByPath(object, objectPath.slice(0, -1));
              target[objectPath[objectPath.length - 1]] = val;
            }
            else {
              object[objectPath[0]] = val;
            }
          }
          else {
            object = val;
          }
        },

        set root (rootObject) {
          object = rootObject;
        }
      };
    },

    /**
     * Ensures that the given path exists in the given object.
     * Any missing piece of the path will be created with objects.
     *
     * Parameter 'object' - The object to create the path into.
     * Parameter 'path' - The path to create in the given object. Should be either a string
     * or an array of strings. If the path is a string, the '/' character functions as a
     * separator for path segments.
     *
     * Returns the input object, modified.
     *
     * NOTE: creating a path where individual path segments actually contain the '/' character
     * is only possible by using the array path syntax.
     *
     * Examples:
     *
     * - makeObjectStructureFromPath(object, 'first')
     * - makeObjectStructureFromPath(object, 'first/second')
     * - makeObjectStructureFromPath(object, ['first'])
     * - makeObjectStructureFromPath(object, ['first', 'second'])
     * - makeObjectStructureFromPath(object, ['first', 'second/third'])
     */
    makeObjectStructureFromPath: function (object, path) {
      if (object == null || path == null || path.length === 0) {
        return object;
      }

      var valueObject = object;
      var objectPath = (typeof path === 'string') ? path.split('/') : path;

      for (var i = 0; i < objectPath.length; i++) {
        var pathSegment = objectPath[i];

        if (!has(valueObject, pathSegment)) {
          valueObject[pathSegment] = {};
        }

        valueObject = valueObject[pathSegment];
      }

      return object;
    },

    /**
     * Returns the data found in the given object at the given path.
     *
     * Parameter 'object' - The object to look into for the requested data.
     * Parameter 'path' - The path into the object at which location to find the requested data.
     * Should be either a string or an array of strings. If the path is a string, the '/' character
     * functions as a separator for path segments.
     *
     * Returns undefined if the given path doesn't exist in the given object.
     *
     * Returns the input object if the path is empty.
     * Returns the input object if either the input object or the path is null or undefined.
     *
     * NOTE: addressing a path where individual path segments actually contain the '/' character
     * is only possible by using the array path syntax.
     *
     * Examples:
     *
     * - getByPath(object, 'first')
     * - getByPath(object, 'first/second')
     * - getByPath(object, ['first'])
     * - getByPath(object, ['first', 'second'])
     * - getByPath(object, ['first', 'second/third'])
     */
    getByPath: function (object, path) {
      if (object == null || path == null || path.length === 0) {
        return object;
      }

      var objectPath = (typeof path === 'string') ? path.split('/') : path;

      var pathSegments = objectPath.slice(0);
      var chain = pathSegments.slice(0, -1);

      var valueObject = object;

      for (var i = 0; i < chain.length; i++) {
        var pathSegment = chain[i];

        if (!has(valueObject, pathSegment)) {
          return undefined;
        }

        valueObject = valueObject[pathSegment];
      }

      return valueObject[pathSegments.pop()];
    },

    /**
     * Converts a path to array form.
     *
     * Parameter 'path' should be either a string or an array of strings. If the path is a string, the '/' character
     * functions as a separator for path segments.
     *
     * Returns an empty path if the path is empty.
     * Returns an empty path if path is null or undefined.
     */
    toPathArray: function (path) {
      if (path == null || path.length === 0) {
        return [];
      }

      return (typeof path === 'string') ? path.split('/') : path;
    }
  };

  /**
   * Utility AngularJS related functions.
   */
  atex.aceui.AngularUtils = {
    /**
     * Forces an Angular digest cycle for the given scope if one is not
     * already in progress. Executes the given function either way.
     */
    apply: function (scope, fn) {
      if (!scope.$$phase) {
        scope.$apply(fn);
      } else {
        fn();
      }
    },

    /**
     * Custom implementation of Q.allSettled (since we don't use Q in
     * the Angular world, and the $q Angular service doesn't have such
     * functionality).
     */
    allSettled: function (q, promises) {
      if (!Array.isArray(promises)) {
        throw Error('atex.aceui.AngularUtils.applyingQ.allSettled requires input to be an array!');
      }

      if (promises.length === 0) {
        return q.when([]);
      }

      var deferred = q.defer();

      var states = [];
      var results = [];

      var checkForCompletion = () => {
        if (states.every((state) => state)) {
          deferred.resolve(results);
        }
      };

      promises.forEach((promise, key) => {
        states[key] = false;

        promise.then((result) => {
          states[key] = true;
          results[key] = { state: 'fulfilled', value: result };

          checkForCompletion();
        }, (error) => {
          states[key] = true;
          results[key] = { state: 'rejected', reason: error };

          checkForCompletion();
        });
      });

      return deferred.promise;
    },

    /**
     * Takes a result from allSettled() and produce an object like:
     *
     * {
     *   fulfilled: ArrayOfFullfillments,
     *   rejected: ArrayOfRejections
     * }
     *
     */
    splitSettled: function (settled) {
      return {
        fulfilled: settled
          .filter((result) => result.state === 'fulfilled')
          .map((result) => result.value),
        rejected: settled
          .filter((result) => result.state === 'rejected')
          .map((result) => result.reason)
      };
    },

    /**
     * Wraps q (can be Q or Angular $q) so that all resolve and reject
     * operations will force a digest cycle (using the apply function
     * in this util object). This is needed because Angular only
     * progresses down the promise chain during digests.
     */
    applyingQ: function (q, scope) {
      return {
        defer: function () {
          var deferred = q.defer();

          return {
            resolve: function (arg) {
              atex.aceui.AngularUtils.apply(scope, function () {
                deferred.resolve(arg);
              });
            },

            reject: function (arg) {
              atex.aceui.AngularUtils.apply(scope, function () {
                deferred.reject(arg);
              });
            },

            promise: deferred.promise
          };
        },

        reject: function (arg) {
          return q.reject(arg);
        },

        all: function (promises) {
          return q.all(promises);
        },

        allSettled: function (promises) {
          return atex.aceui.AngularUtils.allSettled.call(atex.aceui.AngularUtils, this, promises);
        },

        when: function (value) {
          return q.when(value);
        }
      };
    }
  };

  /**
   * Utility HTTP handling functions.
   */

  var HTTPCodeMapping = {
    0: atex.aceui.error.types.UNKNOWN,
    401: atex.aceui.error.types.AUTHENTICATION_FAILED,
    403: atex.aceui.error.types.PERMISSION_DENIED,
    404: atex.aceui.error.types.NOT_FOUND,
    409: atex.aceui.error.types.CONFLICT,
    500: atex.aceui.error.types.SERVER_ERROR,
    502: atex.aceui.error.types.BAD_GATEWAY
  };

  atex.aceui.HTTPUtils = {
    /**
     * Creates and returns an error object for the given jqXHR object. Uses the
     * jqXHR.status.
     *
     * Argument 'error' - An error whith an optional status property to use when creating the error object.
     * Argument 'origin' - An object representing the origin of the error, such as a service.
     * Argument 'causes' - A collection of error messages, keyed on HTTP status code.
     */
    createError: function (error, origin, causes = []) {
      const status = error.status || (error.rootCause && error.rootCause.status) || 0;
      const { severity = atex.aceui.error.severities.MAJOR } = error;

      return atex.aceui.error.Error ({
        origin,
        type: HTTPCodeMapping[status],
        cause: causes[status] || causes.default || 'Just because.',
        rootCause: error,
        severity
      });
    },

    /**
     * If value is a number or can be converted to a number (like the string '123'), returns a number.
     * Otherwise, returns null.
     */
    getAsNumberOrNull: function (value) {
      if (value != null) {
        var number = parseInt(value);
        return isNaN(number) ? null : number;
      }

      return null;
    },

    /**
     * Append earl parameters to the earl.
     */
    withUrlParameters: function (url, parameters) {
      var urlWithParameters = url.slice(0);

      Object.keys(parameters).forEach(function (parameterKey) {
        if (typeof parameters[parameterKey] !== 'undefined') {
          var separator = (urlWithParameters.indexOf('?') === -1) ? '?' : '&';
          urlWithParameters += (separator + encodeURIComponent(parameterKey) + '=' + encodeURIComponent(parameters[parameterKey]));
        }
      });

      return urlWithParameters;
    }
  };

  var fileServiceUriRegex = /^([a-zA-Z0-9]+):([a-zA-Z0-9-_.@]*?)(\/.*$|$)/;

  atex.aceui.FileServiceUtils = {
    /**
     * Validates a File Service URI.
     *
     * Argument 'uriString' - The URI string you want to validate.
     */
    isValidUri: function (uriString) {
      return fileServiceUriRegex.test(uriString);
    },

    /**
     * Parses a File Service URI into a JavaScript object with properties for space, owner and path.
     *
     * If any of the properties are not found, they will not be present in the returned object.
     * If the uriString parameter is not found to be a File Service URI, undefined will be returned.
     *
     * Argument 'uriString' - The URI string you want to validate.
     */
    parseUri: function (uriString) {
      if (typeof uriString !== 'string' || !atex.aceui.FileServiceUtils.isValidUri(uriString)) {
        return undefined;
      }

      var matches = uriString.match(fileServiceUriRegex);

      if (matches === null || matches.length < 1) {
        return undefined;
      }

      var retObj = {};

      if (typeof matches[1] !== 'undefined' && matches[1] !== '') {
        retObj.space = matches[1];
      }

      if (typeof matches[2] !== 'undefined' && matches[2] !== '') {
        retObj.owner = matches[2];
      }

      if (typeof matches[3] !== 'undefined' && matches[3] !== '') {
        retObj.path = matches[3];
      }

      return retObj;
    }
  };

  atex.aceui.ServiceUtils = {
    /**
     * Attempts to calculate a service URL given a service configuration object.
     *
     * Argument 'serviceConfiguration' - The service configuration to calculate the URL from.
     *
     * Will calculate the service URL using the following prioritization:
     *
     * (1) serviceConfiguration.url
     * (2) serviceConfiguration.common.baseUrl + '/' + serviceConfiguration.path
     * (3) defaultPath (if it begins with '/')
     * (4) serviceConfiguration.common.baseUrl + '/' + defaultPath (if defaultPath does not begin with '/')
     *
     * Returns the calculated service URL, or undefined if no URL can be calculated.
     */
    calculateServiceUrl: function (serviceConfiguration, defaultPath) {
      var trimStartingSlashes = function (path) {
        return path.replace(/^\/+/g, '');
      };

      var trimTrailingSlashes = function (path) {
        return path.replace(/\/+$/g, '');
      };

      var trimSlashes = function (path) {
        return trimTrailingSlashes(trimStartingSlashes(path));
      };

      var defaultBasePath = '/ace';
      serviceConfiguration = serviceConfiguration || { common: { baseUrl: defaultBasePath } };
      serviceConfiguration.common = serviceConfiguration.common || { baseUrl: defaultBasePath };

      var baseUrl = trimTrailingSlashes(serviceConfiguration.common.baseUrl);

      var serviceUrl = undefined;

      if (typeof serviceConfiguration.url !== 'undefined') {
        serviceUrl = serviceConfiguration.url;
      }
      else if (typeof serviceConfiguration.path !== 'undefined') {
        serviceUrl = baseUrl + '/' + trimSlashes(serviceConfiguration.path);
      }
      else if (defaultPath) {
        if (defaultPath.startsWith('/')) {
          serviceUrl = defaultPath;
        }
        else {
          serviceUrl = baseUrl + '/' + defaultPath;
        }
      }

      return serviceUrl;
    }
  };

  /**
   * Utility functions for dealing with widgets.
   */
  atex.aceui.WidgetUtils = {
    /**
     * Builds a basic validation function to be used by simple widgets that only has one domain object.
     * The generated function will produce a ValidationResult (see {@link register}) by validating that the
     * domainObject has non-empty data.
     *
     * In widgets where non-empty data is actually valid, the widget should do its own validation.
     *
     * @param domainObject {object} The domain object to validate
     * @param message {string} Optional message to pass in case the validation fails.
     * @return {Function}
     */
    buildBasicValidationFn: function (domainObject, message) {
      return function () {
        // Here we would like to call domainObject.validate(), but that does not exist yet.

        var result = {
          isValid: false
        };

        var data = domainObject.getData();
        if (Array.isArray(data) || typeof data === 'string')  {
          result.isValid = data.length > 0;
        }
        else {
          result.isValid = data != null;
        }

        if (!result.isValid && typeof message !== 'undefined') {
          result.message = message;
        }

        return result;
      };
    }
  };
})();
